/**
 * The people in the current workspace, for pickers and for putting a name to a
 * user id. Built on `listWorkspaceMembers` (src/lib/workspaceService.ts); this
 * only keeps its answer in React state and adds `nameOf`.
 */

import { useEffect, useMemo, useState } from 'react';
import { listWorkspaceMembers } from '../lib/workspaceService';

export interface WorkspaceMemberOption {
  userId: string;
  name: string;
  email: string;
  role: string;
  status: string;
}

function displayName(name: string, email: string): string {
  const n = name.trim();
  if (n) return n;
  const local = email.split('@')[0]?.trim();
  return local || 'Someone';
}

export function useWorkspaceMembers(workspaceId: string | null | undefined): {
  members: WorkspaceMemberOption[];
  activeMembers: WorkspaceMemberOption[];
  nameOf: (userId: string | null | undefined) => string;
  isLoading: boolean;
} {
  const [members, setMembers] = useState<WorkspaceMemberOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!workspaceId) {
      setMembers([]);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    listWorkspaceMembers(workspaceId)
      .then((rows) => {
        if (cancelled) return;
        setMembers(
          rows.map((m) => ({
            userId: m.userId,
            name: displayName(m.user.name, m.user.email),
            email: m.user.email,
            role: String(m.role),
            status: String(m.status),
          })),
        );
      })
      .catch((err) => {
        console.error('[useWorkspaceMembers] failed to list members:', err);
        if (!cancelled) setMembers([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  const byId = useMemo(() => new Map(members.map((m) => [m.userId, m])), [members]);
  const activeMembers = useMemo(() => members.filter((m) => m.status === 'active'), [members]);
  const nameOf = (userId: string | null | undefined) => (userId ? byId.get(userId)?.name ?? 'Someone' : 'nobody');

  return { members, activeMembers, nameOf, isLoading };
}
